/**
 * SEO Sprint 3 (Feb 2026) — Related destinations strip.
 *
 * Universal "Andere populaire bestemmingen" component used on:
 *   - Destination pages (excludes current slug)
 *   - Service pages (sea-freight, air-freight, shop-drop-ship)
 *   - Tool/info pages (paklijst, documenten, faq, hubs, contact)
 *
 * Strategy
 * --------
 * - Hardcoded list of ALL 5 Caribbean destinations (the 4 CMS-driven
 *   + Curaçao which lives on its own legacy page). Hardcoding the list
 *   is intentional: it keeps the internal-linking footprint stable for
 *   PageRank purposes and avoids a network round-trip on every page.
 * - Pluggable `anchorTemplate` so the same component can render
 *   "verzending naar X", "zeevracht naar X", "luchtvracht naar X",
 *   "Shop, Drop & Ship naar X", "paklijst voor zending naar X", etc.
 * - Optional `excludeSlug` so the destination page itself is hidden
 *   from its own related list.
 *
 * No CTA, no buttons — pure visual sitemap cards that subtly support
 * the page's content goal.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";

// Hardcoded canonical destination index (single source of truth for
// internal-linking strategy). Curaçao is included because the
// /shipping-to-curacao page is a primary SEO asset even though it's
// not part of the destinations_cms factory.
export const DESTINATION_LINKS = [
  { slug: "curacao",      name: "Curaçao",      url: "/shipping-to-curacao" },
  { slug: "aruba",        name: "Aruba",        url: "/shipping-to-aruba" },
  { slug: "bonaire",      name: "Bonaire",      url: "/shipping-to-bonaire" },
  { slug: "sint-maarten", name: "Sint Maarten", url: "/shipping-to-sint-maarten" },
  { slug: "suriname",     name: "Suriname",     url: "/shipping-to-suriname" },
];

export default function RelatedDestinations({
  title = "Andere populaire bestemmingen",
  eyebrow = "Bestemmingen",
  description = "Ook regelmatig verzonden vanuit Nederland — bekijk wat wij naar de overige Caribbean-bestemmingen verzorgen.",
  anchorTemplate = "Verzenden naar {name}",
  excludeSlug = null,
  testid = "related-destinations",
  variant = "card", // "card" | "pill" — pill is compact for inline use
}) {
  const items = DESTINATION_LINKS.filter((d) => d.slug !== excludeSlug);
  if (items.length === 0) return null;

  if (variant === "pill") {
    return (
      <div
        className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6"
        data-testid={testid}
      >
        {title && (
          <h3 className="font-chivo font-bold text-base sm:text-lg text-[#082c50] tracking-tight mb-3">
            {title}
          </h3>
        )}
        <ul className="flex flex-wrap gap-2">
          {items.map((d) => (
            <li key={d.slug}>
              <Link
                to={d.url}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#082c50] hover:text-white text-sm font-semibold text-[#082c50] transition-colors"
                data-testid={`${testid}-pill-${d.slug}`}
              >
                <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                {anchorTemplate.replace("{name}", d.name)}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Phase 20E — dynamic grid cols zodat 4-item layouts (na exclude) niet
  // 1 lege cel hebben. 5 items (sea-freight, faq, etc.) → 5 cols, 4 items
  // (op een destination page) → 4 cols. Sub-md blijft 1-2 cols voor mobile.
  const lgColsClass = items.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4";

  // Card variant
  return (
    <section
      className="bg-[#F8FAFC] border-t border-slate-100 py-14 sm:py-16"
      data-testid={testid}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-8">
          {eyebrow ? (
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
              {eyebrow}
            </span>
          ) : null}
          {title ? (
            <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
              {title}
            </h2>
          ) : null}
          {description ? (
            <p className="mt-3 text-[#514e4e] leading-relaxed">
              {description}
            </p>
          ) : null}
        </div>
        <ul
          className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${lgColsClass} gap-3`}
          data-testid={`${testid}-list`}
        >
          {items.map((d) => (
            <li key={d.slug} className="min-w-0">
              <Link
                to={d.url}
                className="group flex items-start justify-between gap-2 bg-white border border-slate-200 rounded-2xl px-4 py-3.5 hover:border-[#082c50]/40 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-18px_rgba(8,44,80,0.3)] transition-all min-w-0"
                data-testid={`${testid}-card-${d.slug}`}
              >
                <span className="inline-flex items-start gap-2 text-sm font-semibold text-[#082c50] min-w-0 flex-1">
                  <MapPin className="w-4 h-4 text-[#1a5193] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="break-words leading-snug min-w-0">
                    {anchorTemplate.replace("{name}", d.name)}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-[#1a5193] flex-shrink-0 mt-1 group-hover:translate-x-1 transition-transform" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
